// src/core/adapters/LondonArrivals.js
//
// TfL-shaped arrival and schedule transforms. Private to LondonAdapter.
// Pure functions. No React, no styles, no I/O.

// Keeps only timing-point stops (type '1') for the requested direction.
export const filterStopsByDirection = (stopData, direction) => {
  const list = direction === 'outbound'
    ? (stopData.outbound || [])
    : (stopData.inbound || []);
  return list.filter(s => String(s.type) === '1');
};

// Builds a naptan_id → internal stop id map.
export const buildNaptanStopIdxMap = (stops) => {
  const map = new Map();
  stops.forEach(stop => {
    map.set(stop.naptan_id, stop.id);
  });
  return map;
};

// Builds a tripMap keyed by trip_no_lbsl from schedule rows, filtered
// to the requested direction. UI direction is 'outbound' | 'inbound';
// the API encodes it as 1 | 2.
export const buildTripMap = (scheduleRows, direction) => {
  const targetDirNum = direction === 'outbound' ? 1 : 2;
  const filtered = scheduleRows.filter(row => Number(row.direction) === targetDirNum);

  const tripMap = {};
  filtered.forEach(row => {
    const key = String(row.trip_no_lbsl);
    if (!tripMap[key]) {
      tripMap[key] = {
        trip_no_lbsl: row.trip_no_lbsl,
        running_no: '-',
        bonnet_no: '-',
        times: {},
        scheduleHeadway: {},
      };
    }
    const trip = tripMap[key];
    trip.times[row.stop_point_idx] = [row.arrival_time.slice(0, 5), null];
    if (row.schedule_headway != null) {
      trip.scheduleHeadway[row.stop_point_idx] = row.schedule_headway;
    }
  });
  return tripMap;
};

// Merges live arrival predictions into the tripMap in place.
// Predictions with no matching trip are dropped.
export const mergeArrivalsIntoTrips = (tripMap, arrivals, naptanToStopIdx) => {
  arrivals.forEach(pred => {
    const tripKey = String(pred.trip_no_lbsl);
    const trip = tripMap[tripKey];
    if (!trip) return;
    if (pred.running_no) trip.running_no = pred.running_no;
    if (pred.bonnet_no) trip.bonnet_no = pred.bonnet_no;
    const stopIdx = naptanToStopIdx.get(pred.naptan_id);
    if (stopIdx != null && trip.times[stopIdx]) {
      trip.times[stopIdx][1] = pred.expected_arrival;
    }
  });
};

// Builds a sorted list of live arrivals for a single stop.
export const buildStopArrivals = (arrivalsData) => {
  const arrivals = (arrivalsData || []).map(item => ({
    trip_no_lbsl: item.trip_no_lbsl,
    running_no: item.running_no || '-',
    bonnet_no: item.bonnet_no || '-',
    expected_arrival: item.expected_arrival,
  }));
  return arrivals
    .filter(a => a.expected_arrival)
    .sort((a, b) => a.expected_arrival.localeCompare(b.expected_arrival));
};

// Filters the full schedule to a single stop and dedupes by trip.
export const buildStopSchedules = (fullScheduleData, stopId) => {
  const raw = Array.isArray(fullScheduleData)
    ? fullScheduleData
    : (fullScheduleData?.data || []);

  const stopIdx = Number(stopId);
  const filtered = raw.filter(s => Number(s.stop_point_idx) === stopIdx);

  const schedules = filtered.map(item => ({
    trip_no_lbsl: item.trip_no_lbsl,
    arrival_time: item.arrival_time?.slice(0, 5) || '--:--',
  }));

  const seen = new Set();
  return schedules.filter(s => {
    if (seen.has(s.trip_no_lbsl)) return false;
    seen.add(s.trip_no_lbsl);
    return true;
  });
};